import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../api';
import { ContactMessage, AdminStats } from '../types';
import UserManagement from './UserManagement';
import PostManagement from './PostManagement';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'demoRequests' | 'users' | 'posts'>('dashboard');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [messagesData, statsData] = await Promise.all([
        adminAPI.getMessages(),
        adminAPI.getStats(),
      ]);
      setMessages(messagesData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await adminAPI.markMessageAsRead(id);
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, isRead: true } : msg
      ));
      if (stats) {
        setStats({ ...stats, unreadMessages: stats.unreadMessages - 1 });
      }
    } catch (error) {
      console.error('Failed to mark message as read:', error);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await adminAPI.deleteMessage(id);
      const deletedMessage = messages.find(msg => msg.id === id);
      setMessages(messages.filter(msg => msg.id !== id));
      if (stats && deletedMessage) {
        setStats({
          ...stats,
          totalMessages: stats.totalMessages - 1,
          unreadMessages: deletedMessage.isRead ? stats.unreadMessages : stats.unreadMessages - 1,
        });
      }
      setSelectedMessage(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <div className="flex items-center space-x-4">
            <span className="text-slate-300">Welcome, {user?.name}</span>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="flex space-x-1 mt-4">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'dashboard'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('demoRequests')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'demoRequests'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            Demo Requests {stats && stats.unreadMessages > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                {stats.unreadMessages}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'users'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'posts'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            Posts / Feed
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1">
        {activeTab === 'dashboard' && (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-slate-400 text-sm font-medium">Total Messages</h3>
                    <p className="text-3xl font-bold text-white mt-2">{stats.totalMessages}</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-slate-400 text-sm font-medium">Unread Messages</h3>
                    <p className="text-3xl font-bold text-orange-400 mt-2">{stats.unreadMessages}</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-slate-400 text-sm font-medium">Total Users</h3>
                    <p className="text-3xl font-bold text-blue-400 mt-2">{stats.totalUsers}</p>
                  </div>
                  <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                    <h3 className="text-slate-400 text-sm font-medium">Total Files</h3>
                    <p className="text-3xl font-bold text-green-400 mt-2">{stats.totalFiles}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Messages Section */}
            <div className="px-6 pb-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="bg-slate-800 rounded-xl border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-semibold">Demo Requests</h2>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="p-6 text-center text-slate-400">
                  No demo requests found
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors ${
                      selectedMessage?.id === message.id ? 'bg-slate-700/50' : ''
                    } ${!message.isRead ? 'border-l-4 border-l-orange-400' : ''}`}
                    onClick={() => setSelectedMessage(message)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-white">{message.name}</h3>
                      <span className="text-xs text-slate-400">
                        {formatDate(message.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 mb-1">{message.email}</p>
                    {message.company && (
                      <p className="text-sm text-slate-400 mb-2">{message.company}</p>
                    )}
                    <p className="text-sm text-slate-400 truncate">
                      {message.message}
                    </p>
                    {!message.isRead && (
                      <span className="inline-block mt-2 px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                        Unread
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Message Detail */}
          <div className="bg-slate-800 rounded-xl border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-semibold">Message Details</h2>
            </div>
            {selectedMessage ? (
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-400">Name</label>
                    <p className="text-white font-medium">{selectedMessage.name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Email</label>
                    <p className="text-white">{selectedMessage.email}</p>
                  </div>
                  {selectedMessage.company && (
                    <div>
                      <label className="text-sm text-slate-400">Company</label>
                      <p className="text-white">{selectedMessage.company}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-slate-400">Message</label>
                    <p className="text-white whitespace-pre-wrap">{selectedMessage.message}</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Received</label>
                    <p className="text-white">{formatDate(selectedMessage.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Status</label>
                    <p className={`font-medium ${selectedMessage.isRead ? 'text-green-400' : 'text-orange-400'}`}>
                      {selectedMessage.isRead ? 'Read' : 'Unread'}
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  {!selectedMessage.isRead && (
                    <button
                      onClick={() => handleMarkAsRead(selectedMessage.id)}
                      className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      Mark as Read
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteMessage(selectedMessage.id)}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors"
                  >
                    Delete
                  </button>
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: Demo Request&body=Hi ${selectedMessage.name},%0D%0A%0D%0AThank you for your interest in our demo.%0D%0A%0D%0ABest regards,%0D%0AAlenis Team`}
                    className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg transition-colors"
                  >
                    Reply via Email
                  </a>
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-slate-400">
                Select a message to view details
              </div>
            )}
          </div>
        </div>
            </div>
            </>
          )}

          {activeTab === 'demoRequests' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-white mb-2">Demo Requests</h2>
                <p className="text-slate-400">Manage and respond to demo requests from potential clients</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Messages List */}
                <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700">
                  <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-white">All Requests</h3>
                      <p className="text-sm text-slate-400 mt-1">
                        {messages.filter(m => !m.isRead).length} unread of {messages.length} total
                      </p>
                    </div>
                    <button
                      onClick={loadData}
                      className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      Refresh
                    </button>
                  </div>
                  <div className="max-h-[600px] overflow-y-auto">
                    {messages.length === 0 ? (
                      <div className="p-12 text-center text-slate-400">
                        <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-lg font-medium">No demo requests yet</p>
                        <p className="text-sm mt-1">New requests will appear here</p>
                      </div>
                    ) : (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-6 border-b border-slate-700 cursor-pointer hover:bg-slate-700/30 transition-colors ${
                            selectedMessage?.id === message.id ? 'bg-slate-700/50 border-l-4 border-l-indigo-500' : ''
                          } ${!message.isRead ? 'border-l-4 border-l-orange-400 bg-orange-500/5' : ''}`}
                          onClick={() => setSelectedMessage(message)}
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="font-semibold text-white text-lg">{message.name}</h3>
                                {!message.isRead && (
                                  <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                                    New
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-blue-400">{message.email}</p>
                              {message.company && (
                                <p className="text-sm text-slate-400 mt-1">
                                  <span className="font-medium">Company:</span> {message.company}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-slate-500 whitespace-nowrap ml-4">
                              {formatDate(message.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-300 line-clamp-2">
                            {message.message}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Message Detail */}
                <div className="lg:col-span-1">
                  {selectedMessage ? (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 sticky top-6">
                      <div className="p-6 border-b border-slate-700">
                        <h3 className="text-xl font-semibold text-white mb-1">Request Details</h3>
                        <p className="text-sm text-slate-400">View and manage this request</p>
                      </div>
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="text-xs text-slate-500 uppercase font-semibold">Contact Person</label>
                          <p className="text-white font-medium mt-1">{selectedMessage.name}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase font-semibold">Email</label>
                          <a href={`mailto:${selectedMessage.email}`} className="text-blue-400 hover:text-blue-300 block mt-1">
                            {selectedMessage.email}
                          </a>
                        </div>
                        {selectedMessage.company && (
                          <div>
                            <label className="text-xs text-slate-500 uppercase font-semibold">Company</label>
                            <p className="text-white mt-1">{selectedMessage.company}</p>
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-slate-500 uppercase font-semibold">Message</label>
                          <p className="text-slate-300 mt-1 whitespace-pre-wrap">{selectedMessage.message}</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 uppercase font-semibold">Received</label>
                          <p className="text-slate-400 mt-1">{formatDate(selectedMessage.createdAt)}</p>
                        </div>
                        <div className="pt-4 border-t border-slate-700">
                          <label className="text-xs text-slate-500 uppercase font-semibold block mb-2">Status</label>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            selectedMessage.isRead 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-orange-500/20 text-orange-400'
                          }`}>
                            {selectedMessage.isRead ? 'Read' : 'Unread'}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 border-t border-slate-700 space-y-3">
                        {!selectedMessage.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(selectedMessage.id)}
                            className="w-full bg-indigo-600 hover:bg-indigo-700 px-4 py-3 rounded-lg transition-colors font-medium"
                          >
                            Mark as Read
                          </button>
                        )}
                        <a
                          href={`mailto:${selectedMessage.email}?subject=Re: Demo Request from ${selectedMessage.company || selectedMessage.name}&body=Hi ${selectedMessage.name},%0D%0A%0D%0AThank you for your interest in Alenis and requesting a demo.%0D%0A%0D%0AWe'd love to show you how our AI-powered document processing can transform your workflow.%0D%0A%0D%0ABest regards,%0D%0AAlenis Team`}
                          className="block w-full text-center bg-green-600 hover:bg-green-700 px-4 py-3 rounded-lg transition-colors font-medium"
                        >
                          Reply via Email
                        </a>
                        <button
                          onClick={() => handleDeleteMessage(selectedMessage.id)}
                          className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 px-4 py-3 rounded-lg transition-colors font-medium border border-red-600/30"
                        >
                          Delete Request
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                      <svg className="w-16 h-16 mx-auto mb-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <p className="text-slate-400">Select a request to view details</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="p-6">
              <UserManagement />
            </div>
          )}

          {activeTab === 'posts' && <PostManagement />}
        </div>
      </div>
  );
};

export default Dashboard;
